/**
 * Exchange Rate Cache - Tracks daily USD to ZAR conversion
 * Caches the rate for the entire day for billing accuracy
 */

const CACHE_KEY = 'sfp_exchange_rate_cache'
const RATE_FETCH_URL = 'https://api.exchangerate-api.com/v4/latest/USD'

class ExchangeRateCache {
  constructor() {
    this.cache = this.loadCache()
  }

  loadCache() {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        return JSON.parse(cached)
      }
    } catch (err) {
      console.warn('Failed to load exchange rate cache:', err)
    }
    return { date: null, rate: null }
  }

  saveCache(date, rate) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ date, rate }))
      this.cache = { date, rate }
    } catch (err) {
      console.warn('Failed to save exchange rate cache:', err)
    }
  }

  isCacheValid() {
    if (!this.cache.date || !this.cache.rate) return false

    // Check if cache is from today
    const cacheDate = new Date(this.cache.date)
    const today = new Date()

    return (
      cacheDate.getFullYear() === today.getFullYear() &&
      cacheDate.getMonth() === today.getMonth() &&
      cacheDate.getDate() === today.getDate()
    )
  }

  async fetchRate() {
    try {
      const response = await fetch(RATE_FETCH_URL, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) {
        throw new Error(`Exchange rate API error: ${response.status}`)
      }

      const data = await response.json()
      const rate = data.rates?.ZAR

      if (!rate) {
        throw new Error('ZAR rate not found in API response')
      }

      // Save to cache with today's date
      const today = new Date().toISOString().split('T')[0]
      this.saveCache(today, parseFloat(rate.toFixed(4)))

      console.log(`✓ Exchange rate updated: 1 USD = R${rate.toFixed(2)}`)
      return rate
    } catch (err) {
      console.warn('Failed to fetch exchange rate:', err)
      // Return fallback rate if API fails
      return this.getFallbackRate()
    }
  }

  getFallbackRate() {
    // Fallback to cached rate if available, otherwise use a reasonable default
    if (this.cache.rate) {
      console.log(`Using cached rate: 1 USD = R${this.cache.rate.toFixed(2)}`)
      return this.cache.rate
    }
    // Safe fallback - approximately current rate
    console.warn('Using fallback exchange rate: 1 USD = R18.50')
    return 18.5
  }

  async getRate() {
    // Return cached rate if valid
    if (this.isCacheValid()) {
      console.log(`Using cached rate: 1 USD = R${this.cache.rate.toFixed(2)}`)
      return this.cache.rate
    }

    // Otherwise fetch new rate
    return await this.fetchRate()
  }

  getCurrentCachedRate() {
    return this.cache.rate || this.getFallbackRate()
  }

  clearCache() {
    localStorage.removeItem(CACHE_KEY)
    this.cache = { date: null, rate: null }
    console.log('✓ Exchange rate cache cleared')
  }
}

export const exchangeRateCache = new ExchangeRateCache()
